
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  QuotaAndGroupUsage, UsageRepositoryInterface,
  WebsiteUsage,
} from '@/services/Usage/interfaces';
import UsageRepository from "@/services/Usage";

@Component({
  inject: ['usageRepository']
})
export default class DiskUsageTab extends Vue {
  @Prop({ required: true }) readonly usage!: QuotaAndGroupUsage | null;

  private readonly usageRepository!: UsageRepositoryInterface;

  private expandedWebsiteIds: number[] = [];

  private activeSections: Record<number, 'databases' | 'mailboxes'> = {};

  private syncingQuotas = false;

  get sites(): WebsiteUsage[] {
    return this.usage?.websiteGroupSnapshot?.websiteUsage || [];
  }

  get totalQuota(): number {
    return this.usage?.quotaSnapshot?.blockHard || 0;
  }

  get totalUsed(): number {
    if (!this.usage) {
      return 0;
    }

    return this.usage.quotaSnapshot.blockUsed;
  }

  get progressPercent(): number {
    if (!this.totalQuota) return 0;
    return Math.min((this.totalUsed / this.totalQuota) * 100, 100);
  }

  get updateIntervalMinutes(): number {
    return (this.usage?.quotaSnapshot?.interval || 600) / 60;
  }

  get totalDatabasesUsed(): number {
    return this.sites.reduce((sum, site) => sum + this.getDatabasesSize(site), 0);
  }

  get totalMailboxesUsed(): number {
    return this.sites.reduce((sum, site) => sum + this.getMailboxesSize(site), 0);
  }

  get totalUsedWithDatabases(): number {
    return this.totalUsed + this.totalDatabasesUsed;
  }

  get totalWithDatabasesPercent(): number {
    if (!this.totalQuota) {
      return 0;
    }

    return Math.min((this.totalUsedWithDatabases / this.totalQuota) * 100, 100);
  }

  public goToEditSystemUser(): void {
    if (!this.usage?.systemUserId) {
      this.$errorDialog.show('System user was not found for this website group.');
      return;
    }

    this.$router.push({
      name: 'system_user_edit',
      params: { systemUserId: String(this.usage.systemUserId) },
    });
  }

  public goToFtpUsers(): void {
    this.$router.push({
      name: 'ftp_user_list',
    });
  }

  private setActiveSection(
    websiteId: number,
    section: 'databases' | 'mailboxes',
  ): void {
    this.$set(this.activeSections, websiteId, section);
  }

  private getActiveSection(websiteId: number): 'databases' | 'mailboxes' {
    return this.activeSections[websiteId] || 'databases';
  }

  private getDatabasesSize(site: WebsiteUsage): number {
    return (site.databasesUsage || []).reduce((sum, db) => sum + (db.size || 0), 0);
  }

  private getMailboxesSize(site: WebsiteUsage): number {
    return (site.mailboxUsage || []).reduce((sum, mailbox) => sum + (mailbox.blockUsed || 0), 0);
  }

  private getTotalSiteSize(site: WebsiteUsage): number {
    return (site.size || 0) + this.getMailboxesSize(site) + this.getDatabasesSize(site);
  }

  private async syncQuotas(): Promise<void> {
    if (!this.usage || this.syncingQuotas) {
      return;
    }

    this.syncingQuotas = true;

    try {
      await this.usageRepository.syncWebsiteGroupDiskUsageByGroupId(this.usage.groupId);

      this.$successDialog.show('Quota synchronization started successfully.');
    } catch (err) {
      this.$errorDialog.show(
        err?.response?.data?.error ||
        err?.message ||
        'Failed to start quota synchronization.',
      );
    } finally {
      this.syncingQuotas = false;
    }
  }

  private toggleExpanded(websiteId: number): void {
    if (this.isExpanded(websiteId)) {
      this.expandedWebsiteIds = this.expandedWebsiteIds.filter(id => id !== websiteId);
      return;
    }

    if (!this.activeSections[websiteId]) {
      this.$set(this.activeSections, websiteId, 'databases');
    }

    this.expandedWebsiteIds = [...this.expandedWebsiteIds, websiteId];
  }

  private isExpanded(websiteId: number): boolean {
    return this.expandedWebsiteIds.includes(websiteId);
  }

  private formatSize(value: number): string {
    if (!value || value <= 0) {
      return '0 KB';
    }

    const kb = value / 1024;
    const mb = value / 1024 / 1024;
    const gb = value / 1024 / 1024 / 1024;

    if (mb < 1) {
      return `${kb.toFixed(2)} KB`;
    }

    if (gb < 1) {
      return `${mb.toFixed(2)} MB`;
    }

    return `${gb.toFixed(2)} GB`;
  }
}
